import { ExternalGitProvider, ExternalGitProviderType } from '@prophecy/graphql-types/enums';
import { ExtractArrayType } from '@prophecy/interfaces/generic';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { UserProviderType } from 'frontend/core/src/context/user';
import { getGithubOAuthInfo, oAuthGithubAuthenticate } from 'frontend/core/src/data/apis/api';

import { SecuredGlobalConfigType } from '../../../context/globalConfig/securedConfigHook';
import { openOauthWindow } from '../../oAuth/utils';
import { GitCreds, GitOAuthResponse } from './types';

export function isProphecyManaged<T extends { externalGitProvider: ExternalGitProviderType }>(gitCred: T) {
  return gitCred.externalGitProvider === ExternalGitProvider.ProphecyManaged;
}

export const loginWithGithub = async (callback: Function, toggleLoader: Function) => {
  const { data: oAuthResp, message } = (await oAuthGithubAuthenticate()) as {
    data: { action: string };
    message?: string;
  };
  if (!oAuthResp?.action) {
    toast.error({ content: message || 'Some error occurred.' });
  } else {
    openOauthWindow(
      oAuthResp.action,
      (payload: { code: string }) => {
        callback(payload?.code);
      },
      toggleLoader
    );
  }
};

export const fetchGitInfo = async (
  userGitCred: ExtractArrayType<UserProviderType['user']['gitSavedCreds']>
): Promise<GitOAuthResponse> => {
  const { externalGitProvider, _id: credId } = userGitCred;

  switch (externalGitProvider) {
    case ExternalGitProvider.Github:
    case ExternalGitProvider.GithubEnterprise: {
      const {
        data: { repositories }
      } = (await getGithubOAuthInfo({
        userGitCredsUID: Number(credId),
        isGithubEnterprise: false
      })) as { data: GitOAuthResponse };

      return { ...userGitCred, repositories } as unknown as GitOAuthResponse;
    }

    default: {
      return userGitCred as unknown as GitOAuthResponse;
    }
  }
};

export const gitCredDetails = (userGitCred: GitOAuthResponse & { userGitCredsUID?: string }): GitCreds => {
  if (userGitCred._id || userGitCred.userGitCredsUID) {
    return { userGitCredsUID: Number(userGitCred._id || userGitCred.userGitCredsUID) };
  }

  return {
    externalRepoAccessToken: userGitCred.externalRepoAccessToken,
    externalRepoUsername: userGitCred.externalRepoUsername,
    externalRepoEmail: userGitCred.externalRepoEmail,
    externalGitProvider: userGitCred.externalGitProvider
  };
};

export const defaultGitProvider = (globalConf: SecuredGlobalConfigType) => {
  const githubOptions = globalConf.getGitProviderOptions;
  const defaultOption = githubOptions?.[0] ?? ExternalGitProvider.GithubEnterprise;
  return globalConf.isGithubEnabled ? ExternalGitProvider.Github : defaultOption;
};
